import { useEffect } from 'react';
import { useHub } from '@/contexts/HubContext';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { isDemoFeatureEnvironment } from '@/lib/hub-environment';
import { hasModuleAdminAccess } from '@/lib/module-permissions';
import { setPresentationModeEnabled, usePresentationMode } from './store';

/** A client-track membership: a client role, or any module's client-view / client-admin gate. */
export function isClientUser(roles: readonly string[], permissions: readonly string[]): boolean {
  return (
    roles.some((role) => role.includes('client')) ||
    permissions.some((permission) => permission.endsWith(':client-view') || permission.endsWith(':client-admin'))
  );
}

export interface DemoModeAccessInput {
  environmentAllowed: boolean;
  isAnalyticsAdmin: boolean;
  isClientUser: boolean;
  hasAllClientsAccess: boolean;
}

/** Demo mode is an operator-side tool: never for client users, nor for anyone scoped to named clients. */
export function canUseDemoMode(input: DemoModeAccessInput): boolean {
  return input.environmentAllowed && input.isAnalyticsAdmin && !input.isClientUser && input.hasAllClientsAccess;
}

export interface DemoModeAccess {
  allowed: boolean;
  /** False until permissions and the client scope have loaded; `allowed` is false meanwhile. */
  resolved: boolean;
}

export function useDemoModeAccess(): DemoModeAccess {
  const { roles, permissions, hasPermission, isLoading: permissionsLoading } = useUserPermissions();
  const { hasAllClientsAccess, clients, isLoading: hubLoading } = useHub();
  const allowed = canUseDemoMode({
    environmentAllowed: isDemoFeatureEnvironment(),
    isAnalyticsAdmin: hasModuleAdminAccess(hasPermission, 'analytics'),
    isClientUser: isClientUser(roles, permissions),
    hasAllClientsAccess,
  });
  // The loading flags drop between the operator and client fetches, so the client scope counts as
  // known only once the fetch has returned something.
  const resolved = !permissionsLoading && !hubLoading && (hasAllClientsAccess || clients.length > 0);
  return { allowed, resolved };
}

/** Switch Demo mode off for a session whose user may not use it: local storage may carry the flag from another user of the browser. */
export function useEnforceDemoModeAccess(): void {
  const { allowed, resolved } = useDemoModeAccess();
  const { enabled } = usePresentationMode();
  useEffect(() => {
    if (resolved && !allowed && enabled) setPresentationModeEnabled(false);
  }, [resolved, allowed, enabled]);
}
